import React, { useState, useRef, useEffect, useCallback } from "react";
import "./App.css";

const API = "https://thewalldb.onrender.com/api";
const TILE_W = 260;
const TILE_H = 300;

const POSTER_STYLES = [
  { bg: "#050505", text: "#e8e8e8", dim: "#444" },
  { bg: "#0f0f0f", text: "#e8e8e8", dim: "#444" },
  { bg: "#141414", text: "#e8e8e8", dim: "#555" },
  { bg: "#e8e8e8", text: "#0a0a0a", dim: "#888" },
  { bg: "#f0f0f0", text: "#0a0a0a", dim: "#888" },
  { bg: "#1a1a1a", text: "#d0d0d0", dim: "#505050" },
  { bg: "#080808", text: "#c0c0c0", dim: "#3a3a3a" },
  { bg: "#eaeaea", text: "#111",    dim: "#999" },
];
const FONTS = ["'Syne', sans-serif", "'Space Mono', monospace"];

// ─── HELPERS ─────────────────────────────────────────────
function getToken()       { return localStorage.getItem("ow_token"); }
function setToken(t)      { localStorage.setItem("ow_token", t); }
function clearToken()     { localStorage.removeItem("ow_token"); }
function authHeaders()    { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

// ─── TILE ────────────────────────────────────────────────
function Tile({ tile, onClick, isHighlighted, onBeaconToggle, isOwner, onEdit }) {
  const style    = POSTER_STYLES[tile.colorIndex % POSTER_STYLES.length];
  const font     = FONTS[tile.fontIndex % FONTS.length];
  const isCollab = tile.openToCollab;

  return (
    <div
      className={`tile ${isHighlighted ? "tile--highlighted" : ""} ${isCollab ? "tile--collab" : ""}`}
      style={{ left: tile.x, top: tile.y, background: style.bg, fontFamily: font }}
      onClick={() => onClick(tile)}
    >
      {isCollab && <div className="tile__beacon-ring" />}
      <div className="tile__inner">
        <div className="tile__top-row">
          <div className="tile__number" style={{ color: style.dim }}>#{String(tile.wallNumber).padStart(3,"0")}</div>
          {isCollab && <div className="tile__collab-dot" />}
        </div>

        {tile.avatar
          ? <img src={tile.avatar} alt="" className="tile__avatar" style={{ borderColor: style.dim }} />
          : <div className="tile__avatar-placeholder" style={{ borderColor: style.dim, color: style.dim }}>{tile.name?.[0]?.toUpperCase()||"?"}</div>
        }

        <div className="tile__name" style={{ color: style.text }}>{tile.name||"Anonymous"}</div>
        <div className="tile__role" style={{ color: style.dim }}>{tile.role||"Creator"}</div>

        <div className="tile__tags">
          {(tile.tags||[]).slice(0,3).map((tag,i)=>(
            <span key={i} className="tile__tag" style={{ borderColor: style.dim, color: style.dim }}>{tag}</span>
          ))}
        </div>

        {tile.location && (
          <div className="tile__location" style={{ color: style.dim, borderTopColor: style.dim+"33" }}>{tile.location}</div>
        )}
      </div>

      {isOwner && (
        <div className="tile__owner-controls">
          <button className="tile__owner-btn" onClick={e=>{e.stopPropagation();onEdit(tile);}}>EDIT</button>
          <button className={`tile__owner-btn ${isCollab?"tile__owner-btn--beacon":""}`}
            onClick={e=>{e.stopPropagation();onBeaconToggle(tile);}}>
            {isCollab?"⬡ ON":"⬡"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MODAL ───────────────────────────────────────────────
function Modal({ tile, onClose, onBeaconToggle, isOwner, onEdit }) {
  const style = POSTER_STYLES[tile.colorIndex % POSTER_STYLES.length];
  const font  = FONTS[tile.fontIndex % FONTS.length];

  useEffect(()=>{
    const h = e => e.key==="Escape" && onClose();
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${tile.openToCollab?"modal--collab":""}`}
        style={{ background: style.bg, fontFamily: font }}
        onClick={e=>e.stopPropagation()}>

        <button className="modal__close" style={{ color: style.dim, borderColor: style.dim+"55" }} onClick={onClose}>✕</button>

        {tile.openToCollab && (
          <div className="modal__collab-banner">
            <span className="modal__collab-pulse" />
            OPEN TO COLLAB — reach out
          </div>
        )}

        <div className="modal__header" style={{ borderBottomColor: style.dim+"22" }}>
          {tile.avatar
            ? <img src={tile.avatar} alt="" className="modal__avatar" style={{ borderColor: style.dim+"55" }} />
            : <div className="modal__avatar-placeholder" style={{ borderColor: style.dim+"55", color: style.dim }}>{tile.name?.[0]?.toUpperCase()||"?"}</div>
          }
          <div>
            <div className="modal__number" style={{ color: style.dim }}>#{String(tile.wallNumber).padStart(3,"0")}</div>
            <div className="modal__name"   style={{ color: style.text }}>{tile.name}</div>
            <div className="modal__role"   style={{ color: style.dim }}>{tile.role}</div>
            {tile.location && <div className="modal__location" style={{ color: style.dim }}>{tile.location}</div>}
          </div>
        </div>

        {tile.bio && <p className="modal__bio" style={{ color: style.dim }}>{tile.bio}</p>}

        {tile.projects?.filter(p=>p.name).length>0 && (
          <div className="modal__section">
            <div className="modal__section-title" style={{ color: style.dim, borderBottomColor: style.dim+"22" }}>PROJECTS</div>
            {tile.projects.filter(p=>p.name).map((p,i)=>(
              <div key={i} className="modal__project" style={{ borderBottomColor: style.dim+"18" }}>
                <div className="modal__project-name" style={{ color: style.text }}>{p.name}</div>
                {p.desc && <div className="modal__project-desc" style={{ color: style.dim }}>{p.desc}</div>}
                {p.url  && <a href={p.url} target="_blank" rel="noreferrer" className="modal__project-link" style={{ color: style.dim, borderBottomColor: style.dim+"44" }}>{p.url} ↗</a>}
              </div>
            ))}
          </div>
        )}

        {(tile.links?.twitter||tile.links?.github||tile.links?.website) && (
          <div className="modal__links">
            {tile.links.twitter && <a href={tile.links.twitter} target="_blank" rel="noreferrer" className="modal__link" style={{ borderColor: style.dim+"44", color: style.dim }}>Twitter</a>}
            {tile.links.github  && <a href={tile.links.github}  target="_blank" rel="noreferrer" className="modal__link" style={{ borderColor: style.dim+"44", color: style.dim }}>GitHub</a>}
            {tile.links.website && <a href={tile.links.website} target="_blank" rel="noreferrer" className="modal__link" style={{ borderColor: style.dim+"44", color: style.dim }}>Website</a>}
          </div>
        )}

        {isOwner && (
          <div className="modal__owner-actions">
            <button className="modal__beacon-toggle modal__beacon-toggle--edit" onClick={()=>{onClose();onEdit(tile);}}>
              ✎ EDIT MY TILE
            </button>
            <button className={`modal__beacon-toggle ${tile.openToCollab?"modal__beacon-toggle--on":""}`}
              onClick={()=>onBeaconToggle(tile)}>
              {tile.openToCollab ? "⬡ TURN OFF COLLAB BEACON" : "⬡ TURN ON COLLAB BEACON"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── AUTH MODAL ───────────────────────────────────────────
function AuthModal({ mode: initMode, onClose, onAuth }) {
  const [mode, setMode]   = useState(initMode); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(()=>{
    const h = e => e.key==="Escape" && onClose();
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[onClose]);

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      const endpoint = mode==="login" ? "/auth/login" : "/auth/register";
      const res  = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error||"Something went wrong"); setLoading(false); return; }
      setToken(data.token);
      onAuth(data);
    } catch { setError("Cannot reach server"); }
    setLoading(false);
  };

  const onKey = e => e.key==="Enter" && submit();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e=>e.stopPropagation()}>
        <button className="modal__close" onClick={onClose}>✕</button>

        <div className="auth-modal__tabs">
          <button className={`auth-tab ${mode==="login"?"auth-tab--active":""}`} onClick={()=>setMode("login")}>LOGIN</button>
          <button className={`auth-tab ${mode==="register"?"auth-tab--active":""}`} onClick={()=>setMode("register")}>REGISTER</button>
        </div>

        <div className="auth-modal__body">
          {mode==="register" && <div className="auth-modal__hint">Choose your username — it's permanent.</div>}

          <input
            className="create-input" placeholder="Username"
            value={username} onChange={e=>setUsername(e.target.value)} onKeyDown={onKey}
            autoFocus autoComplete="username"
          />
          <input
            className="create-input" placeholder="Password" type="password"
            value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={onKey}
            autoComplete={mode==="login"?"current-password":"new-password"}
          />

          {error && <div className="auth-modal__error">{error}</div>}

          <button className="create-btn" onClick={submit} disabled={!username||!password||loading}>
            {loading ? "..." : mode==="login" ? "LOGIN →" : "CREATE ACCOUNT →"}
          </button>

          <div className="auth-modal__switch">
            {mode==="login"
              ? <span>No account? <button onClick={()=>setMode("register")}>Register</button></span>
              : <span>Have an account? <button onClick={()=>setMode("login")}>Login</button></span>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── TILE FORM (create + edit) ────────────────────────────
function TileForm({ onClose, onSaved, existingTile }) {
  const isEdit = !!existingTile;
  const [step, setStep]             = useState(1);
  const [colorIndex, setColorIndex] = useState(existingTile?.colorIndex ?? 0);
  const [fontIndex,  setFontIndex]  = useState(existingTile?.fontIndex  ?? 0);
  const [loading, setLoading]       = useState(false);
  const [form, setForm] = useState({
    name:         existingTile?.name        || "",
    role:         existingTile?.role        || "",
    bio:          existingTile?.bio         || "",
    location:     existingTile?.location    || "",
    avatar:       existingTile?.avatar      || "",
    tags:         existingTile?.tags?.join(", ") || "",
    openToCollab: existingTile?.openToCollab ?? false,
    projects:     existingTile?.projects?.length ? existingTile.projects : [{ name:"", desc:"", url:"" }],
    links:        existingTile?.links || { twitter:"", github:"", website:"" },
  });

  const u  = (k,v) => setForm(f=>({...f,[k]:v}));
  const ul = (k,v) => setForm(f=>({...f,links:{...f.links,[k]:v}}));
  const up = (i,k,v) => setForm(f=>{ const p=[...f.projects]; p[i]={...p[i],[k]:v}; return{...f,projects:p}; });

  const style = POSTER_STYLES[colorIndex];
  const font  = FONTS[fontIndex];

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t=>t.trim()).filter(Boolean),
        colorIndex, fontIndex,
      };
      let res;
      if (isEdit) {
        res = await fetch(`${API}/tiles/${existingTile._id}`, { method:"PUT", headers: authHeaders(), body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${API}/tiles`, { method:"POST", headers: authHeaders(), body: JSON.stringify(payload) });
      }
      const data = await res.json();
      if (!res.ok) { alert(data.error||"Error saving tile"); setLoading(false); return; }
      onSaved(data);
    } catch { alert("Cannot reach server"); }
    setLoading(false);
  };

  return (
    <div className="create-overlay" onClick={onClose}>
      <div className="create-modal" onClick={e=>e.stopPropagation()}>

        {/* LEFT */}
        <div className="create-modal__left">
          <div className={`create-preview ${form.openToCollab?"create-preview--collab":""}`}
            style={{ background: style.bg, fontFamily: font }}>
            {form.openToCollab && <div className="tile__beacon-ring" />}
            <div className="tile__inner" style={{ padding:"14px 12px 10px" }}>
              <div className="tile__top-row">
                <div className="tile__number" style={{ color: style.dim }}>#???</div>
                {form.openToCollab && <div className="tile__collab-dot" />}
              </div>
              <div className="tile__avatar-placeholder" style={{ borderColor: style.dim+"55", color: style.dim, width:28, height:28, fontSize:12 }}>
                {form.name?.[0]?.toUpperCase()||"?"}
              </div>
              <div className="tile__name" style={{ color: style.text, fontFamily: font, fontSize:14 }}>{form.name||"YOUR NAME"}</div>
              <div className="tile__role" style={{ color: style.dim }}>{form.role||"your role"}</div>
              <div className="tile__tags">
                {form.tags.split(",").filter(Boolean).slice(0,3).map((t,i)=>(
                  <span key={i} className="tile__tag" style={{ borderColor: style.dim+"44", color: style.dim }}>{t.trim()}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="create-palette">
            <div className="create-label">SHADE</div>
            <div className="create-colors">
              {POSTER_STYLES.map((s,i)=>(
                <div key={i} className={`create-color ${i===colorIndex?"create-color--active":""}`}
                  style={{ background: s.bg, border:`1px solid ${s.text}22` }}
                  onClick={()=>setColorIndex(i)} />
              ))}
            </div>
            <div className="create-label">FONT</div>
            <div className="create-fonts">
              {FONTS.map((f,i)=>(
                <div key={i} className={`create-font ${i===fontIndex?"create-font--active":""}`}
                  style={{ fontFamily: f }} onClick={()=>setFontIndex(i)}>Aa</div>
              ))}
            </div>
            <div className="create-label" style={{ marginTop:8 }}>COLLAB BEACON</div>
            <button className={`create-beacon-toggle ${form.openToCollab?"create-beacon-toggle--on":""}`}
              onClick={()=>u("openToCollab",!form.openToCollab)}>
              {form.openToCollab?"⬡ BEACON ON":"⬡ BEACON OFF"}
            </button>
            <div className="create-beacon-hint">
              {form.openToCollab?"Your tile pulses. Others can filter to find you.":"Toggle if you want co-founders or collabs."}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="create-modal__right">
          <div className="create-header">
            <div className="create-title">{isEdit?"EDIT YOUR TILE":"CLAIM YOUR SPOT"}</div>
            <button className="create-close" onClick={onClose}>✕</button>
          </div>

          <div className="create-steps">
            {[1,2,3].map(s=>(
              <div key={s} className={`create-step ${s===step?"active":""} ${s<step?"done":""}`} onClick={()=>setStep(s)}>{s}</div>
            ))}
          </div>

          {step===1 && (
            <div className="create-fields">
              <div className="create-label">WHO ARE YOU?</div>
              <input className="create-input" placeholder="Full Name *" value={form.name} onChange={e=>u("name",e.target.value)} />
              <input className="create-input" placeholder="Role / Title *" value={form.role} onChange={e=>u("role",e.target.value)} />
              <input className="create-input" placeholder="Location — city, country" value={form.location} onChange={e=>u("location",e.target.value)} />
              <textarea className="create-textarea" placeholder="Short bio..." value={form.bio} onChange={e=>u("bio",e.target.value)} />
              <input className="create-input" placeholder="Avatar URL (optional)" value={form.avatar} onChange={e=>u("avatar",e.target.value)} />
              <input className="create-input" placeholder="Tags, comma separated" value={form.tags} onChange={e=>u("tags",e.target.value)} />
              <button className="create-btn" onClick={()=>setStep(2)}>NEXT →</button>
            </div>
          )}

          {step===2 && (
            <div className="create-fields">
              <div className="create-label">YOUR PROJECTS</div>
              {form.projects.map((p,i)=>(
                <div key={i} className="create-project-block">
                  <input className="create-input" placeholder={`Project ${i+1} name`} value={p.name} onChange={e=>up(i,"name",e.target.value)} />
                  <input className="create-input" placeholder="One-line description"  value={p.desc} onChange={e=>up(i,"desc",e.target.value)} />
                  <input className="create-input" placeholder="URL"                   value={p.url}  onChange={e=>up(i,"url", e.target.value)} />
                </div>
              ))}
              {form.projects.length<4 && (
                <button className="create-btn create-btn--secondary"
                  onClick={()=>setForm(f=>({...f,projects:[...f.projects,{name:"",desc:"",url:""}]}))}>
                  + ADD PROJECT
                </button>
              )}
              <button className="create-btn" onClick={()=>setStep(3)}>NEXT →</button>
            </div>
          )}

          {step===3 && (
            <div className="create-fields">
              <div className="create-label">LINKS</div>
              <input className="create-input" placeholder="Twitter" value={form.links.twitter} onChange={e=>ul("twitter",e.target.value)} />
              <input className="create-input" placeholder="GitHub"  value={form.links.github}  onChange={e=>ul("github", e.target.value)} />
              <input className="create-input" placeholder="Website" value={form.links.website} onChange={e=>ul("website",e.target.value)} />
              <button className="create-btn" onClick={submit} disabled={!form.name||!form.role||loading}>
                {loading ? "SAVING..." : isEdit ? "SAVE CHANGES ✦" : "PLACE ON THE WALL ✦"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── APP ─────────────────────────────────────────────────
export default function App() {
  const canvasRef = useRef(null);
  const [tiles, setTiles]           = useState([]);
  const [offset, setOffset]         = useState({ x: 80, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart]   = useState(null);
  const [selectedTile, setSelectedTile] = useState(null);
  const [zoom, setZoom]             = useState(1);
  const [search, setSearch]         = useState("");
  const [highlightedId, setHighlightedId] = useState(null);
  const [collabOnly, setCollabOnly] = useState(false);

  // auth state
  const [authUser, setAuthUser]     = useState(null); // { userId, username, tileId }
  const [showAuth, setShowAuth]     = useState(null); // "login" | "register" | null
  const [showTileForm, setShowTileForm] = useState(false);
  const [editingTile, setEditingTile]   = useState(null);

  // ── init: load tiles + restore session ──
  useEffect(()=>{
    fetch(`${API}/tiles`).then(r=>r.json()).then(setTiles).catch(()=>setTiles([]));
    if (getToken()) {
      fetch(`${API}/auth/me`, { headers: authHeaders() })
        .then(r=>r.ok ? r.json() : null)
        .then(data => { if (data) setAuthUser(data); else clearToken(); })
        .catch(()=>{});
    }
  },[]);

  // ── drag ──
  const onMouseDown = useCallback(e=>{
    if (e.target.closest(".tile")||e.target.closest(".ui-bar")) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX-offset.x, y: e.clientY-offset.y });
  },[offset]);

  const onMouseMove = useCallback(e=>{
    if (!isDragging||!dragStart) return;
    setOffset({ x: e.clientX-dragStart.x, y: e.clientY-dragStart.y });
  },[isDragging,dragStart]);

  const onMouseUp = useCallback(()=>{ setIsDragging(false); setDragStart(null); },[]);

  // ── zoom ──
  const onWheel = useCallback(e=>{
    e.preventDefault();
    setZoom(z=>Math.min(2,Math.max(0.25,z-e.deltaY*0.001)));
  },[]);

  useEffect(()=>{
    const el=canvasRef.current; if(!el) return;
    el.addEventListener("wheel",onWheel,{passive:false});
    return ()=>el.removeEventListener("wheel",onWheel);
  },[onWheel]);

  // ── auth ──
  const handleAuth = (data) => {
    setAuthUser({ userId: data.userId, username: data.username, tileId: data.tileId });
    setShowAuth(null);
    // if new user with no tile, open tile form
    if (!data.tileId) setTimeout(()=>setShowTileForm(true), 300);
  };

  const handleLogout = () => {
    clearToken();
    setAuthUser(null);
  };

  // ── beacon ──
  const handleBeaconToggle = async (tile) => {
    try {
      const res  = await fetch(`${API}/tiles/${tile._id}/beacon`, { method:"PATCH", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { alert(data.error); return; }
      setTiles(ts=>ts.map(t=>t._id===data._id?data:t));
      if (selectedTile?._id===data._id) setSelectedTile(data);
    } catch { alert("Could not toggle beacon"); }
  };

  // ── create / edit ──
  const handleSaved = (tile) => {
    setTiles(ts=>{
      const exists = ts.find(t=>t._id===tile._id);
      return exists ? ts.map(t=>t._id===tile._id?tile:t) : [...ts, tile];
    });
    setAuthUser(u=>({...u, tileId: tile._id}));
    setShowTileForm(false);
    setEditingTile(null);
    setHighlightedId(tile._id);
    setOffset({
      x: -tile.x*zoom + window.innerWidth /2 - TILE_W/2,
      y: -tile.y*zoom + window.innerHeight/2 - TILE_H/2,
    });
    setTimeout(()=>setHighlightedId(null),3000);
  };

  const openEdit = (tile) => { setEditingTile(tile); setShowTileForm(true); };

  // ── search ──
  const handleSearch = e=>{
    const val=e.target.value; setSearch(val);
    if(!val){ setHighlightedId(null); return; }
    const found=tiles.find(t=>
      t.name?.toLowerCase().includes(val.toLowerCase())||
      t.role?.toLowerCase().includes(val.toLowerCase())||
      t.tags?.some(tag=>tag.toLowerCase().includes(val.toLowerCase()))
    );
    if(found){
      setHighlightedId(found._id);
      setOffset({ x:-found.x*zoom+window.innerWidth/2-TILE_W/2, y:-found.y*zoom+window.innerHeight/2-TILE_H/2 });
    }
  };

  const collabCount  = tiles.filter(t=>t.openToCollab).length;
  const myTileId     = authUser?.tileId?.toString();

  const visibleTiles = tiles.filter(t=>{
    const ms = !search||t.name?.toLowerCase().includes(search.toLowerCase())||t.role?.toLowerCase().includes(search.toLowerCase())||t.tags?.some(tag=>tag.toLowerCase().includes(search.toLowerCase()));
    const mc = !collabOnly||t.openToCollab;
    return ms&&mc;
  });

  // what to show in CTA
  const ctaLabel = !authUser
    ? "CLAIM YOUR SPOT ✦"
    : authUser.tileId
      ? "MY TILE"
      : "PLACE YOUR TILE ✦";

  const handleCta = () => {
    if (!authUser) { setShowAuth("register"); return; }
    if (authUser.tileId) {
      const myTile = tiles.find(t=>t._id===authUser.tileId?.toString()||t._id?.toString()===authUser.tileId?.toString());
      if (myTile) openEdit(myTile);
      return;
    }
    setShowTileForm(true);
  };

  return (
    <div className="app" ref={canvasRef}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove}
      onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
      style={{ cursor: isDragging?"grabbing":"grab" }}>

      <div className="grid-bg" style={{ backgroundPosition:`${offset.x%60}px ${offset.y%60}px` }} />

      <div className="canvas"
        style={{ transform:`translate(${offset.x}px,${offset.y}px) scale(${zoom})`, transformOrigin:"0 0" }}>
        {visibleTiles.map(tile=>(
          <Tile key={tile._id} tile={tile}
            onClick={setSelectedTile}
            isHighlighted={tile._id===highlightedId}
            onBeaconToggle={handleBeaconToggle}
            onEdit={openEdit}
            isOwner={!!myTileId && (tile._id===myTileId || tile._id?.toString()===myTileId)}
          />
        ))}
      </div>

      {/* TOP BAR */}
      <div className="ui-bar">
        <div className="ui-logo">ONE<span>WALL</span><div className="ui-count">{tiles.length} makers</div></div>

        <input className="ui-search" placeholder="Search by name, role, tag..." value={search} onChange={handleSearch} />

        <button className={`ui-collab-filter ${collabOnly?"ui-collab-filter--on":""}`} onClick={()=>setCollabOnly(c=>!c)}>
          <span className={`ui-collab-filter__dot ${collabOnly?"ui-collab-filter__dot--on":""}`} />
          OPEN TO COLLAB
          {collabCount>0 && <span className="ui-collab-filter__count">{collabCount}</span>}
        </button>

        <div className="ui-controls">
          <button className="ui-zoom" onClick={()=>setZoom(z=>Math.min(2,z+0.15))}>+</button>
          <button className="ui-zoom" onClick={()=>setZoom(z=>Math.max(0.25,z-0.15))}>−</button>
          <button className="ui-zoom" onClick={()=>{setZoom(1);setOffset({x:80,y:80});}}>⌂</button>
        </div>

        {/* auth + cta */}
        <div className="ui-auth">
          {authUser ? (
            <div className="ui-user">
              <span className="ui-username">@{authUser.username}</span>
              <button className="ui-logout" onClick={handleLogout}>LOG OUT</button>
            </div>
          ) : (
            <button className="ui-login-btn" onClick={()=>setShowAuth("login")}>LOG IN</button>
          )}
          <button className="ui-cta" onClick={handleCta}>{ctaLabel}</button>
        </div>
      </div>

      <div className="ui-hint">DRAG TO EXPLORE · SCROLL TO ZOOM · CLICK A TILE</div>

      {selectedTile && (
        <Modal tile={selectedTile} onClose={()=>setSelectedTile(null)}
          onBeaconToggle={handleBeaconToggle} onEdit={openEdit}
          isOwner={!!myTileId&&(selectedTile._id===myTileId||selectedTile._id?.toString()===myTileId)}
        />
      )}

      {showAuth && (
        <AuthModal mode={showAuth} onClose={()=>setShowAuth(null)} onAuth={handleAuth} />
      )}

      {showTileForm && (
        <TileForm
          onClose={()=>{setShowTileForm(false);setEditingTile(null);}}
          onSaved={handleSaved}
          existingTile={editingTile}
        />
      )}
    </div>
  );
}